/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import { useGetMaxAmountToWrap } from "hooks/wrap/useGetMaxAmountToWrap";
import { useStoreState } from "pullstate";
import React, { useState } from "react";
import { usePublicClient, useWalletClient } from "wagmi";
import { CounterParty } from "../../../components/TradeLeg/TradeLeg";
import { BalanceDetails } from "../../../components/TradeLeg/sections/BalanceDetails";
import { useBalanceForSymbol } from "../../../hooks/useBalanceForSymbol";
import { GlobalStore } from "../../../store/global/GlobalStore";
import { NETWORK_TO_TOKEN_ADDRESS_MAP, Token } from "../../../store/global/TokenEnum";
import { Balance } from "../../../store/global/getBalances";
import { Amount } from "../../../types/amount";
import { WrapQuote, WrapType, WrapWarningMessages } from "../../../types/wrapQuote";
import { getWrappedSymbolForChain } from "../../../utils/chainUtils";
import { getTokenIcon } from "../../../utils/tokenIconUtils";
import { estimateWrapping, isNativeToken } from "../../../utils/wrapUtils";
import { TokenSection } from "./TokenSection";
import { WrapAmountInput } from "./WrapAmountInput";
import { WrapAmountLabel } from "./WrapAmountLabel";

export interface WrapLegProps {
  token: Token;
  balance: Balance;
  counterParty: CounterParty;
}

export const WrapLeg: React.FC<WrapLegProps> = props => {
  const isAmountInputApplicable = props.counterParty === "taker";
  const tokens = GlobalStore.useState(s => s.tokens);
  const wrapQuote = GlobalStore.useState(s => s.wrapQuote);
  const state = useStoreState(GlobalStore);
  const { data: walletClient } = useWalletClient();
  const publicClient = usePublicClient();
  const maxBalance = useBalanceForSymbol(props.token);
  const getMaxAmountToWrap = useGetMaxAmountToWrap();
  const [isAmountInputMax, setIsAmountInputMax] = useState(false);
  const amountToDisplay = wrapQuote?.amount
    ? wrapQuote.amount.toDisplay(props.token, tokens).toString()
    : Amount.ZERO.toDisplay(props.token, tokens).toString();

  const handleInput = async (newAmount: Amount) => {
    const maxAmountToWrap = await getMaxAmountToWrap(props.token);
    const isMax = maxAmountToWrap ? maxAmountToWrap.value.eq(newAmount.value) : false;

    if (
      walletClient &&
      isMax &&
      state.wrapQuote?.type === "Wrapping" &&
      !newAmount.value.isZero() &&
      tokens
    ) {
      const amountToEstimate = props.balance.balance;
      const gasCost = await estimateWrapping(
        "deposit",
        NETWORK_TO_TOKEN_ADDRESS_MAP[state.network.id][getWrappedSymbolForChain(state.network)],
        amountToEstimate,
        walletClient,
        publicClient,
        getWrappedSymbolForChain(state.network),
        tokens
      );
      newAmount = amountToEstimate.sub(gasCost);
    }

    GlobalStore.update(s => {
      const newWrapQuote: WrapQuote = {
        type: s.wrapQuote ? s.wrapQuote.type : WrapType.Wrapping,
        amount: newAmount,
        max: isMax,
        gas: Amount.ZERO,
        hash: undefined,
        warning: undefined,
        error: undefined,
      };
      // Warning message not needed for unwrapping as it doesn't change whether they can or can't unwrap.
      if (isNativeToken(props.token, state.network) && newAmount.value.gt(0)) {
        if (isAmountInputMax) {
          newWrapQuote.warning = WrapWarningMessages.WARNING;
        }
      }
      s.wrapQuote = newWrapQuote;
    });
  };

  return (
    <div className={`pay-box active symbol-select`}>
      <div className="row swap-token-row g-0 align-items-center">
        <div
          css={css`
            display: flex;
            flex-direction: row;
            flex-wrap: nowrap;
            justify-content: space-between;
            align-content: stretch;
            align-items: center;
          `}
        >
          {" "}
          <TokenSection
            symbol={props.token}
            counterParty={"maker"}
            tokenIcon={getTokenIcon(props.token, tokens)}
          />
          <WrapAmountLabel
            amountToDisplay={amountToDisplay}
            visible={props.counterParty === "maker" && !wrapQuote?.amount?.value.isZero()}
          />
          <WrapAmountInput
            visible={isAmountInputApplicable}
            maxAmount={props.balance}
            wrapQuoteAmount={wrapQuote ? wrapQuote.amount : undefined}
            handleInput={value => void handleInput(value)}
            symbol={props.token}
            setIsMax={setIsAmountInputMax}
          />
        </div>
        <BalanceDetails
          onBalanceClick={() => {
            props.counterParty === "taker" && maxBalance && void handleInput(maxBalance.balance);
          }}
          symbol={props.token}
          balanceToDisplay={
            props.balance.balance.toDisplay(props.token, tokens).toString() ?? {
              symbol: props.token,
              balance: Amount.ZERO,
            }
          }
          price={new Amount("1")}
          amount={Amount.ZERO}
          showBalance={true}
        />
      </div>
    </div>
  );
};
